import {
  Grid,
  withStyles,
  WithStyles,
  withWidth,
  Theme,
  CircularProgress
} from '@material-ui/core'
import { WithWidth } from '@material-ui/core/withWidth'
import arrayMove from 'array-move'
import classNames from 'classnames'
import { ObjectFitProperty } from 'csstype'
import i18n from 'i18next'
import { Component, Fragment, useState, useEffect } from 'react'
import { SortableContainer, SortableElement, SortableHandle } from 'react-sortable-hoc'
import deleteIcon from '../../../images/deleteIcon.svg'
import moveIcon from '../../../images/moveIcon.svg'
import { isSmallScreen } from '../../../libs/screenSize'
import { BucketObject } from '../../../services/storage/types'
import { ApartmentMedia, ApartmentMediaItem } from '../../../types/apartments'
import { ImageFileWithObjectURL } from '../../../types/files'


const styles = ({ breakpoints }: Theme) => {
  const hasSteps = window.location.href.includes("steps");

  return {
    root: {
      maxWidth: '100%',
      padding: '10px 0px 0px 0px'
    },
    rightArrow: {
      display: 'block',
      background: 'grey',
      borderRadius: '40px'
    },
    leftArrow: {
      display: 'block',
      background: 'grey',
      borderRadius: '40px'
    },
    removeIcon: {
      zIndex: 99998,
      position: 'absolute' as const,
      top: '-5px',
      right: '-5px',
      height: '50px',
      width: '50px',
      borderRadius: '50%',
      background: '#fff',
      cursor: 'pointer',
      '& > svg': {
        zIndex: 99999,
        position: 'relative' as const,
        top: '5px',
        left: '5px'
      }
    },
    moveIcon: {
      zIndex: 99998,
      position: 'absolute' as const,
      bottom: '-5px',
      right: '-5px',
      height: '50px',
      width: '50px',
      borderRadius: '50%',
      background: '#fff',
      cursor: 'pointer',
      '&> div': {
        position: 'relative' as const,
        background: '#E7F6E5',
        borderRadius: '50%',
        top: '6px',
        left: '6px',
        width: '38px',
        height: '38px',
        '& > img': {
          zIndex: 99999,
          position: 'relative' as const,
          top: '7px',
          left: '7px'
        }
      }
    },
    imageContainer: {
      position: 'relative' as const,
      width: '100%',
    },
    gallery: {
      display: 'flex',
      flexDirection: 'row',
      gap: '12px',
      overflowX: 'auto',
      justifyContent: 'flex-start',
      maxWidth: '100%',
      [breakpoints.down('xs')]: {
        height: 'auto', // don't set fixed height unless calculated
        flexWrap: 'wrap',
        flexDirection: 'column',
        overflowX: 'hidden',
        overflowY: 'auto',         // ✅ allow vertical scroll on mobile
        WebkitOverflowScrolling: 'touch' // ✅ smooth scroll on iOS
      },
      '&::-webkit-scrollbar': {
        height: '20px',
        color: 'black'
      },
      '&::-webkit-scrollbar-track': {
        boxShadow: 'inset 0 0 5px #e8f6e5',
        borderRadius: '10px'
      },

      '&::-webkit-scrollbar-thumb': {
        background: '#4f9b45',
        borderRadius: '10px'
      },

      '&::-webkit-scrollbar-thumb:hover': {
        background: '#3f7c36'
      }
    },
    thumbnail: {
      height: '150px',          // <-- force height for uniform size
      width: '180px',             // <-- force width for uniform size
      borderRadius: '16px',
      objectFit: 'cover',         // <-- ensures image fills square box
      [breakpoints.down('xs')]: {
        height: 'auto',
        width: '100%'
      }
    },
    itemContainer: {
      margin: '6px',
      width: '180px',
      flex: '0 0 auto',
      position: 'relative' as const,
      padding: '10px 0',
      zIndex: hasSteps ? 1200 : 99999,
      boxSizing: 'border-box',
      [breakpoints.down('xs')]: {
        width: '97%',
        touchAction: 'none',  // ✅ prevents scrolling while dragging
        userSelect: 'none',
      }
    }
  };
};


export const isBucketObject = (
  file: BucketObject | ImageFileWithObjectURL
): file is BucketObject => (file as BucketObject)?.url !== undefined

type MediaFile = BucketObject | ImageFileWithObjectURL

const DragHandle = ({ classes }: { classes: any }) => (
  <span className={classNames(classes.moveIcon, 'drag-handle')}>
    <div>
      <img src={moveIcon} />
    </div>
  </span>
)

const ImageComponent = ({ classes, file, selectKey }: { classes: any, file: any, selectKey: any }) => (
  <img
    className={classNames(classes.thumbnail)}
    src={isBucketObject(file) ? file.url : file.key}
    alt={isBucketObject(file) ? file.key : file.path}
    onClick={() => {
      selectKey(file.key)
    }}
  />
)

const SortableImage = SortableHandle(ImageComponent)
const SortableDragHandle = SortableHandle(DragHandle)

const SortableItem = withStyles(styles)(
  SortableElement(
    ({
      file,
      classes,
      selectedKey,
      selectKey,
      withDelete,
      t
    }: {
      file: MediaFile
      classes: any
      selectedKey: string
      selectKey: (key: string) => void
      withDelete: any
      t: i18n.TFunction
    }) => {
      const [isDeleting, setIsDeleting] = useState(false)

      const handleDelete = async () => {
        if (sessionStorage.getItem('isDeletingImage') === 'true') {
          return
        }
        setIsDeleting(true)
        sessionStorage.setItem('isDeletingImage', 'true')

        try {
          await withDelete.deleteMedia(file.key)
        } catch (error) {
          console.error('Error deleting media:', error)
        } finally {
        }
      }

      return (
        <div className={classes.itemContainer}>
          <SortableImage
            classes={classes}
            file={file}
            selectKey={selectKey}
          />
          <span className={classes.removeIcon} onClick={handleDelete}>
            {isDeleting ? (
              <CircularProgress
                size={28}
                style={{
                  color: '#fff',
                  margin: '6px 0 0 6px',
                  background: '#f44f50',
                  borderRadius: '100px',
                  padding: '5px'
                }}
              />
            ) : (
              <img src={deleteIcon} />
            )}
          </span>
          <SortableDragHandle classes={classes} />
        </div>
      )
    }
  )
)

const SortableList = withStyles(styles)(
  SortableContainer(
    ({
      files,
      selectedKey,
      selectKey,
      classes,
      withDelete,
      t
    }: {
      files: MediaFile[]
      selectedKey: string
      selectKey: (key: string) => void
      classes: any
      withDelete: any
      t: i18n.TFunction
    }) => {
      const [isDeletingImage, setIsDeletingImage] = useState(
        sessionStorage.getItem('isDeletingImage') === 'true'
      )

      useEffect(() => {
        const handleStorageChange = () => {
          setIsDeletingImage(sessionStorage.getItem('isDeletingImage') === 'true')
        }

        window.addEventListener('storage', handleStorageChange)

        return () => {
          window.removeEventListener('storage', handleStorageChange)
        }
      }, [])

      return (
        <Grid className={classes.gallery}>
          {files.map((file: MediaFile, index: number) => (
            <SortableItem
              key={`${index}-${file.key}`}
              index={index}
              file={file}
              selectedKey={selectedKey}
              withDelete={withDelete}
              selectKey={selectKey}
              t={t}
              disabled={isDeletingImage}
            />
          ))}
        </Grid>
      )
    }
  )
)

interface MediaProps extends WithStyles, WithWidth {
  media: ApartmentMedia
  files: MediaFile[] | undefined
  withDelete?: { deleteMedia: (arg: any) => any }
  reorderMedia: (arg: any) => any
  slidesToShow?: number
  t: i18n.TFunction
  /** css height property */
  imageHeight?: string
  /** css width property */
  imageWidth?: string
  /**  objectFit property */
  imageFit?: ObjectFitProperty
}

interface MediaState {
  selectedKey: string
  media: ApartmentMedia
}

class MediaGallery extends Component<MediaProps, MediaState> {
  constructor(props: MediaProps) {
    super(props)
    this.state = {
      media: props.media,
      selectedKey:
        (Array.isArray(props.media) && props.media[0] && props.media[0].key) ||
        ''
    }
  }

  componentDidMount() {
    sessionStorage.removeItem('isDeletingImage')
  }

  slideAmount = (slidesToShow?: number) => slidesToShow || 1

  slidesToShow = () => {
    const slideAmount = this.slideAmount(this.props.slidesToShow)
    const isNormalScreen = !isSmallScreen(this.props.width)
    const slidesToShow =
      this.props.files &&
        this.props.files.length >= slideAmount &&
        isNormalScreen
        ? slideAmount
        : 1

    return slidesToShow
  }

  componentDidUpdate(prevProps: MediaProps) {
    if (
      this.props.media !== prevProps.media ||
      !this.state.media
    ) {
      this.setState({ media: this.props.media });
    }
  }

  render() {
    const {
      files,
      // slidesToShow,
      withDelete,
      reorderMedia,
      t,
      classes
    } = this.props
    const smScreen = window.innerWidth < 600;
    const { media } = this.state
    const selectedKey = this.state.selectedKey || (media && media[0].key) || ''
    let sortedFiles: (BucketObject | ImageFileWithObjectURL)[] = []
    if (Array.isArray(media) && Array.isArray(files)) {
      sortedFiles = media
        .map((mediaItem) => {
          return (
            files.find((file) => file.key === mediaItem.key) ||
            ({} as BucketObject)
          )
        })
        .filter((file) => !!file.key)
    }
    return (
      <Fragment>
        {files && files.length > 0 && (
          <Grid className={classes.root}>
            {/* <Grid className={classes.imageContainer}>
              {withDelete && (
                <Tooltip title={`${t('apartment.mediaUpload.delete')}`}>
                  <span
                    className={classes.removeIcon}
                    onClick={() => {
                      withDelete && withDelete.deleteMedia(selectedKey)
                      this.setState({
                        selectedKey: ''
                      })
                    }}
                  >
                    <DeleteCircleIcon size={40} color='red' />
                  </span>
                </Tooltip>
              )}
              <img
                style={{
                  maxHeight: '360px',
                  height: imageHeight || undefined,
                  width: imageWidth || '100%',
                  margin: '0 auto',
                  objectFit: imageFit || 'contain'
                }}
                className={classes.image}
                src={
                  isBucketObject(
                    selectedFile as BucketObject | ImageFileWithObjectURL
                  )
                    ? (selectedFile as BucketObject).url
                    : selectedFile.key
                }
                alt={
                  isBucketObject(selectedFile)
                    ? selectedFile.key
                    : selectedFile.path
                }
              />
            </Grid> */}
            <Grid>
              <SortableList
                // lockAxis='x'
                useDragHandle
                axis={smScreen ? 'y' : 'xy'}
                lockAxis={smScreen ? 'y' : 'xy'}
                distance={smScreen ? 8 : 0}
                files={sortedFiles}
                selectedKey={selectedKey}
                withDelete={withDelete}
                t={t}
                selectKey={(key) => {
                  this.setState({
                    selectedKey: key
                  })
                }}
                onSortEnd={({ oldIndex, newIndex }) => {
                  this.setState({
                    media: arrayMove<ApartmentMediaItem>(
                      media,
                      oldIndex,
                      newIndex
                    )
                  })
                  reorderMedia({ oldIndex, newIndex })
                }}
              />
            </Grid>
          </Grid>
        )}
      </Fragment>
    )
  }
}

export default withStyles(styles)(withWidth()(MediaGallery))
